import { IonItem } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { BUDGET_TYPES, CredentialItem, RateObjectType } from "model";
import { Card } from "swing-components";

import {
  BillingDetails,
  COMBINED_RESTRICTIONS,
  fetchBillingDetails,
  translate,
  useShouldRestrictAdmin,
} from "~utils";

import styles from "./SchoolBillingDetails.module.css";

type SchoolBillingDetailsProps = {
  payRates: RateObjectType[];
  acceptedCredentials: CredentialItem[];
  schoolId: string;
};

type PayRatesProps = {
  payRates: RateObjectType[];
};

function PayRates({ payRates }: PayRatesProps) {
  return (
    <IonItem lines="none" className="w-full">
      <div className={styles.ratesTable}>
        <div className={`${styles.ratesRow} font-medium`}>
          <div className={styles.ratesCol}>{translate("RATES_LABEL")}</div>
          <div className={styles.daysCol}>{translate("HALF_DAY_LABEL")}</div>
          <div className={styles.daysCol}>{translate("FULL_DAY_LABEL")}</div>
        </div>

        {/* Short Term Rates */}
        {payRates.map((item, index) => {
          return (
            <div className={styles.ratesRow} key={index}>
              <div className={styles.ratesCol}>
                {item.positionName} {translate("SHORT_TERM_SUFFIX")}
              </div>
              <div className={styles.daysCol}>{item.rates.halfDay.amount}</div>
              <div className={styles.daysCol}>{item.rates.fullDay.amount}</div>
            </div>
          );
        })}

        {/* Long Term Rates */}
        {payRates.map((item, index) => {
          return (
            <div className={styles.ratesRow} key={index}>
              <div className={styles.ratesCol}>
                {item.positionName} {translate("LONG_TERM_SUFFIX")}
              </div>
              <div className={styles.daysCol}>{item.rates.halfDayLongTerm.amount}</div>
              <div className={styles.daysCol}>{item.rates.fullDayLongTerm.amount}</div>
            </div>
          );
        })}
      </div>
    </IonItem>
  );
}

export function SchoolBillingDetails({
  payRates,
  acceptedCredentials,
  schoolId,
}: SchoolBillingDetailsProps) {
  const useBillingDetails = (id: string) => {
    return useQuery<BillingDetails>({
      queryKey: ["billingDetails", id],
      queryFn: async () => {
        const { data } = await fetchBillingDetails(id);
        return data;
      },
    });
  };

  const credentialsJoined = acceptedCredentials?.map((item) => item.name)?.join(", ");

  const shouldHideBillingDetails = useShouldRestrictAdmin(COMBINED_RESTRICTIONS.VIEW_SUB_PAY);

  const { data: billingDetails } = useBillingDetails(schoolId);
  const hasPayRates = payRates.length > 0;

  if (shouldHideBillingDetails) {
    return <></>;
  }

  return (
    <div css={{ marginBottom: 16 }}>
      <Card>
        <h2 className="m-0 mb-4">{translate("BILLING_DETAILS_HEADER")}</h2>

        {billingDetails && (
          <div css={{ borderBottom: hasPayRates ? `1px solid rgba(0, 0, 0, 0.13)` : "none" }}>
            <div className="pb-4">
              <p className="font-medium mt-2 mb-2">{translate("INVOICE_TYPE_LABEL")}</p>
              <p>{billingDetails.invoiceType}</p>
            </div>

            {billingDetails.invoiceType !== BUDGET_TYPES.BUDGET && (
              <>
                <p className="font-medium mt-2 mb-2">{translate("PRIMARY_BILLING_EMAIL_LABEL")}</p>
                <p>{billingDetails?.billingEmail || translate("NOT_PROVIDED")}</p>
              </>
            )}
          </div>
        )}
        {hasPayRates && <PayRates payRates={payRates} />}

        {acceptedCredentials?.length > 0 && (
          <IonItem lines="none">
            <div className="pb-4 pt-2">
              <p className="font-medium mt-2 mb-2">{translate("CREDENTIAL_TYPES_LABEL")}</p>
              <p>{credentialsJoined}</p>
            </div>
          </IonItem>
        )}
      </Card>
    </div>
  );
}
